import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DataTable from './common/DataTable';
import Alert from './common/Alert';
import ChatModal from './common/ChatModal';
import AsistenteService from '../services/AsistenteService';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import ProcesosMenu from './common/ProcesosMenu';
import './MyAssistants.css';
import {
  isAdminUser as isAdminUserRole,
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { navigateAdminMenuItem, navigateProfessorMenuItem } from './sidebar/menuConfig';
import {
  normalizeAssistantProvider,
  isAssistantProcessingReady,
  isAssistantProcessingError,
  getAssistantProcessingPercent,
  getAssistantProcessingLabel,
  getAssistantProcessingClassName,
  getAssistantProcessingDetail,
  hasAssistantProcessingWarning
} from '../utils/aveProcessing';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const normalizeSearchText = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const pickAssistantValue = (assistant, ...keys) => {
  for (const key of keys) {
    const value = assistant?.[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }

  return '';
};

const getAssistantDisplayName = (assistant) =>
  pickAssistantValue(assistant, 'nombreAsistente', 'NombreAsistente', 'nombre', 'Nombre') || 'Asistente AVE';

const getAssistantDescription = (assistant) =>
  pickAssistantValue(assistant, 'descripcion', 'Descripcion', 'description', 'Description');

const getAssistantPromptExtra = (assistant) =>
  pickAssistantValue(assistant, 'promptExtra', 'PromptExtra');

const getAssistantAreaName = (assistant) =>
  pickAssistantValue(assistant, 'nombreArea', 'NombreArea');

const getAssistantSubjectName = (assistant) =>
  pickAssistantValue(assistant, 'nombreMateria', 'NombreMateria');

const getAssistantPassword = (assistant) =>
  pickAssistantValue(assistant, 'passwordAsistente', 'PasswordAsistente');

const getAssistantModel = (assistant) =>
  pickAssistantValue(assistant, 'ragFlowModelo', 'RagFlowModelo');

const getAssistantId = (assistant) =>
  pickAssistantValue(assistant, 'idOpenAIAssistant', 'IdOpenAIAssistant');

const MyAssistants = ({ provider = 'openai' }) => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const isProfesor = isProfessorUser(user);
  const isAdminUser = Boolean(user) && isAdminUserRole(user);
  const isAllowedUser = isProfesor || isAdminUser;
  const navigate = useNavigate();
  const isRagFlowProvider = String(provider).toLowerCase() === 'ragflow';
  const providerLabel = isRagFlowProvider ? 'AVE' : 'OpenAI';
  const { featureFlags } = useRegionalFeatureFlags(user, { enabled: !authLoading });
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  const isCurrentAssistantFeatureEnabled = isRagFlowProvider ? isRagFlowAssistantEnabled : isOpenAIAssistantEnabled;
  
  // Estados
  const [asistentes, setAsistentes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [warningModal, setWarningModal] = useState({ open: false, assistantName: '', detail: '' });
  const [hasLoaded, setHasLoaded] = useState(false);
  const isLoadingRef = useRef(false);
  const [assistantToDelete, setAssistantToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => isRagFlowProvider ? 'cards' : 'table');

  useEffect(() => {
    setViewMode(isRagFlowProvider ? 'cards' : 'table');
    setSearchTerm('');
  }, [isRagFlowProvider]);

  // Cargar asistentes al montar el componente
  useEffect(() => {
    if (authLoading) return;

    if (!isAllowedUser) {
      setIsLoading(false);
      return;
    }

    if (!hasLoaded && !isLoadingRef.current) {
      loadAsistentes();
    }
  }, [hasLoaded, isAllowedUser, authLoading, provider]);

  const loadAsistentes = async () => {
    if (authLoading) return;
    if (!isAllowedUser) {
      setIsLoading(false);
      return;
    }
    // Evitar llamadas duplicadas
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setAlert({ show: false, message: '', type: 'error', title: '' });

      if (isRagFlowProvider) {
        await AsistenteService.refrescarEstadosRagFlow()
          .catch((error) => console.warn('No se pudieron refrescar estados AVE:', error));
      }
      
      const response = await AsistenteService.getMisAsistentes();
      
      if (response.ok && response.asistentes) {
        const filtered = response.asistentes.filter((item) => {
          const flag = item?.gptClasicoCreacionFinalizada ?? item?.GPTClasicoCreacionFinalizada;
          const assistantProvider = normalizeAssistantProvider(item?.proveedorIA || item?.ProveedorIA);
          const providerMatches = isRagFlowProvider
            ? assistantProvider === 'ragflow'
            : assistantProvider !== 'ragflow';
          return flag !== true && providerMatches;
        });
        setAsistentes(filtered);
      } else {
        throw new Error(response.error || 'Error al cargar los asistentes');
      }
      
    } catch (error) {
      console.error('Error al cargar asistentes:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes. Inténtalo nuevamente.',
        type: 'error'
      });
      setAsistentes([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
      isLoadingRef.current = false;
    }
  };

  const handleRefresh = () => {
    if (!isAllowedUser) return;
    setHasLoaded(false); // Permitir nueva carga
    isLoadingRef.current = false; // Resetear el flag de carga
    loadAsistentes();
  };

  const handleNewAssistant = () => {
    if ((isRagFlowProvider && !isRagFlowAssistantEnabled) || (!isRagFlowProvider && !isAdminUser && !isCurrentAssistantFeatureEnabled)) {
      if (isRagFlowProvider) {
        setAlert({
          show: true,
          title: 'Funcionalidad deshabilitada',
          message: 'El feature flag de Asistentes AVE no esta habilitado para esta regional.',
          type: 'warning'
        });
        return;
      }
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes OpenAI no está habilitado para esta regional.',
        type: 'warning'
      });
      return;
    }
    navigate(isRagFlowProvider ? '/new-ragflow-assistant' : '/new-assistant');
  };

  const handleLogout = () => {
    logout();
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleTestAssistant = (assistant) => {
    if (isRagFlowProvider && !isAssistantProcessingReady(assistant)) {
      setAlert({
        show: true,
        title: 'Asistente AVE no disponible',
        message: getAssistantProcessingDetail(assistant) || 'El asistente AVE todavia esta procesando documentos en RagFlow.',
        type: 'warning'
      });
      return;
    }

    setSelectedAssistant(assistant);
    setShowChatModal(true);
  };

  const handleReportsAssistant = (assistant) => {
    if (isRagFlowProvider && !isAssistantProcessingReady(assistant)) {
      setAlert({
        show: true,
        title: 'Asistente AVE en proceso',
        message: 'Los reportes estaran disponibles cuando finalice el procesamiento.',
        type: 'warning'
      });
      return;
    }

    navigate('/reports', { state: { assistant } });
  };

  const handleCloseChatModal = () => {
    setShowChatModal(false);
    setSelectedAssistant(null);
  };

  const handleEditAssistant = (assistant) => {
    if (isRagFlowProvider && !isAssistantProcessingReady(assistant)) {
      setAlert({
        show: true,
        title: 'Asistente AVE en proceso',
        message: 'La edicion se habilitara cuando finalice el procesamiento.',
        type: 'warning'
      });
      return;
    }

    const assistantId = getAssistantId(assistant);
    if (!assistantId) return;
    const editPath = isRagFlowProvider ? '/ragflow-assistants' : '/assistants';
    navigate(`${editPath}/${assistantId}/edit`);
  };

  const handleDeleteClick = (assistant) => {
    if (isRagFlowProvider && !isAssistantProcessingReady(assistant) && !isAssistantProcessingError(assistant)) {
      setAlert({
        show: true,
        title: 'Asistente AVE en proceso',
        message: 'La eliminacion se habilitara cuando finalice el procesamiento.',
        type: 'warning'
      });
      return;
    }

    setAssistantToDelete(assistant);
  };

  const isAssistantActive = (assistant) => {
    const value = assistant?.activo ?? assistant?.Activo;
    return value == null ? true : Boolean(value);
  };

  const getAssistantRowClassName = (assistant) =>
    isAssistantActive(assistant) ? '' : 'assistant-row-inactive';

  const openWarningModal = (assistant, detail) => {
    if (!detail) return;
    setWarningModal({
      open: true,
      assistantName: getAssistantDisplayName(assistant),
      detail
    });
  };

  const closeWarningModal = () => {
    setWarningModal({ open: false, assistantName: '', detail: '' });
  };

  const closeDeleteModal = () => {
    setAssistantToDelete(null);
  };

  const filteredAsistentes = useMemo(() => {
    const query = normalizeSearchText(searchTerm);
    if (!query) return asistentes;

    return asistentes.filter((assistant) => {
      const searchableValues = [
        getAssistantDisplayName(assistant),
        getAssistantDescription(assistant),
        getAssistantPromptExtra(assistant),
        getAssistantAreaName(assistant),
        getAssistantSubjectName(assistant),
        getAssistantModel(assistant),
        getAssistantPassword(assistant),
        getAssistantProcessingLabel(assistant),
        getAssistantProcessingDetail(assistant),
        getAssistantId(assistant)
      ];

      return searchableValues.some((value) => normalizeSearchText(value).includes(query));
    });
  }, [asistentes, searchTerm]);

  const hasActiveSearch = searchTerm.trim().length > 0;
  const emptyAssistantsMessage = hasActiveSearch
    ? 'No se encontraron asistentes con ese criterio.'
    : 'No tienes asistentes creados aún. ¡Crea tu primer asistente!';

  const handleConfirmDelete = async () => {
    const assistantId = getAssistantId(assistantToDelete);
    if (!assistantId) return;
    setAssistantToDelete(null);
    try {
      setIsDeleting(true);
      setAlert({
        show: true,
        title: 'Eliminando asistente',
        message: 'Estamos eliminando el asistente seleccionado. Este proceso puede tardar unos segundos.',
        type: 'info'
      });

      await AsistenteService.eliminarAsistenteOpenAI(assistantId);

      setAlert({
        show: true,
        title: 'Asistente eliminado',
        message: 'El asistente se eliminó correctamente.',
        type: 'success'
      });
      setAsistentes(prev => prev.filter(asistente => getAssistantId(asistente) !== assistantId));
    } catch (error) {
      console.error('Error al eliminar asistente:', error);
      setAlert({
        show: true,
        title: 'Error al eliminar asistente',
        message: error.message || 'No se pudo eliminar el asistente. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!isAllowedUser) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Esta sección está disponible únicamente para usuarios con rol Profesor o Admin.</p>
        </div>
      </div>
    );
  }

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    const parsed = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(parsed.getTime())) return '-';

    return parsed.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderProcessingStatus = (assistant) => {
    const label = getAssistantProcessingLabel(assistant);
    const percent = getAssistantProcessingPercent(assistant);
    const detail = getAssistantProcessingDetail(assistant);
    const className = getAssistantProcessingClassName(assistant);
    const hasWarning = isRagFlowProvider && hasAssistantProcessingWarning(assistant);
    const hasError = isRagFlowProvider && isAssistantProcessingError(assistant);
    const warningDetail = detail || 'El asistente tiene advertencias de procesamiento, pero no se recibio un detalle especifico.';
    const shouldShowDetail = Boolean(detail) && hasError && !hasWarning;

    return (
      <div className="ave-processing-cell" title={detail || label}>
        <span className={`ave-processing-badge ${className}`}>{label}</span>
        {hasWarning && (
          <button
            type="button"
            className="ave-processing-warning-badge"
            title="Ver detalle de advertencia"
            aria-label="Ver detalle de advertencia"
            onClick={() => openWarningModal(assistant, warningDetail)}
          >
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            Advertencia
          </button>
        )}
        {percent !== null && (
          <div className="ave-processing-meta">
            <div className="ave-processing-bar" aria-hidden="true">
              <span style={{ width: `${percent}%` }} />
            </div>
            <small>{percent}%</small>
          </div>
        )}
        {shouldShowDetail && (
          <small className={`ave-processing-detail${hasError ? ' error' : ''}`}>{detail}</small>
        )}
      </div>
    );
  };

  const renderAssistantActions = (item, variant = 'table') => {
    const isCard = variant === 'card';
    const actionsClassName = isCard ? 'assistant-card-actions' : 'table-actions';

    if (isRagFlowProvider && !isAssistantProcessingReady(item)) {
      if (isAssistantProcessingError(item)) {
        return (
          <div className={actionsClassName}>
            <button
              className={isCard ? 'assistant-card-action danger' : 'table-btn table-btn-delete'}
              title="Eliminar asistente con error"
              onClick={() => handleDeleteClick(item)}
              type="button"
            >
              <i className="fas fa-trash"></i>
              {isCard && <span>Eliminar</span>}
            </button>
          </div>
        );
      }

      return (
        <span className={isCard ? 'assistant-card-actions-disabled' : 'ave-actions-disabled'}>
          Sin acciones
        </span>
      );
    }

    if (isCard) {
      return (
        <div className={actionsClassName}>
          <button
            className="assistant-card-action primary"
            title="Abrir chat"
            onClick={() => handleTestAssistant(item)}
            type="button"
          >
            <i className="fas fa-comments"></i>
            <span>Chat</span>
          </button>
          <button
            className="assistant-card-action"
            title="Ver reportes"
            onClick={() => handleReportsAssistant(item)}
            type="button"
          >
            <i className="fas fa-chart-bar"></i>
            <span>Reportes</span>
          </button>
          <button
            className="assistant-card-action"
            title="Editar asistente"
            onClick={() => handleEditAssistant(item)}
            type="button"
          >
            <i className="fas fa-pen"></i>
            <span>Editar</span>
          </button>
          <button
            className="assistant-card-action danger"
            title="Eliminar asistente"
            onClick={() => handleDeleteClick(item)}
            type="button"
          >
            <i className="fas fa-trash"></i>
            <span>Eliminar</span>
          </button>
        </div>
      );
    }

    return (
      <div className={actionsClassName}>
        <button
          className="table-btn table-btn-edit"
          title="Editar asistente"
          onClick={() => handleEditAssistant(item)}
        >
          <i className="fas fa-pen"></i>
        </button>
        <button
          className="table-btn table-btn-delete"
          title="Eliminar asistente"
          onClick={() => handleDeleteClick(item)}
        >
          <i className="fas fa-trash"></i>
        </button>
        <button
          className="table-btn table-btn-test"
          title="Probar asistente"
          onClick={() => handleTestAssistant(item)}
        >
          <i className="fas fa-play"></i>
          <span>Probar</span>
        </button>
        <button
          className="table-btn table-btn-reports"
          title="Ver reportes"
          onClick={() => handleReportsAssistant(item)}
        >
          <i className="fas fa-chart-bar"></i>
        </button>
      </div>
    );
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del Asistente',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'nombreArea',
      title: 'Área',
      sortable: true
    },
    {
      key: 'nombreMateria',
      title: 'Materia',
      sortable: true
    },
    {
      key: 'passwordAsistente',
      title: 'Password',
      sortable: false,
      render: (value, item) => item.passwordAsistente || item.PasswordAsistente || '—'
    },
    ...(isRagFlowProvider ? [
      {
        key: 'estadoProcesamiento',
        title: 'Procesamiento',
        sortable: false,
        align: 'center',
        width: 'lg',
        render: (value, item) => renderProcessingStatus(item)
      }
    ] : []),
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => renderAssistantActions(item, 'table')
    }
  ];

  const renderAssistantCards = () => {
    if (isLoading) {
      return (
        <div className="assistant-cards-state">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>Obteniendo mis asistentes...</p>
        </div>
      );
    }

    if (!filteredAsistentes.length) {
      return (
        <div className="assistant-cards-state">
          <div className="empty-icon">
            <i className="fas fa-inbox"></i>
          </div>
          <p>{emptyAssistantsMessage}</p>
        </div>
      );
    }

    return (
      <div className="assistant-cards-grid">
        {filteredAsistentes.map((assistant, index) => {
          const name = getAssistantDisplayName(assistant);
          const description = getAssistantDescription(assistant);
          const areaName = getAssistantAreaName(assistant);
          const subjectName = getAssistantSubjectName(assistant);
          const password = getAssistantPassword(assistant);
          const model = getAssistantModel(assistant);
          const assistantId = getAssistantId(assistant);
          const cardKey = assistantId || assistant.id || assistant.Id || index;
          const isInactive = !isAssistantActive(assistant);

          return (
            <article
              key={cardKey}
              className={`assistant-card${isInactive ? ' inactive' : ''}`}
            >
              <div className="assistant-card-top">
                <div className="assistant-card-identity">
                  <div className="assistant-card-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="assistant-card-title">
                    <h3>{name}</h3>
                    <span>{subjectName || areaName || `Asistente ${providerLabel}`}</span>
                  </div>
                </div>
                {isRagFlowProvider && renderProcessingStatus(assistant)}
              </div>

              <p className={`assistant-card-description${description ? '' : ' muted'}`}>
                {description || 'Sin descripcion cargada.'}
              </p>

              <div className="assistant-card-meta">
                <span>
                  <i className="fas fa-book-open"></i>
                  {subjectName || 'Sin materia'}
                </span>
                <span>
                  <i className="fas fa-layer-group"></i>
                  {areaName || 'Sin area'}
                </span>
                {model && (
                  <span>
                    <i className="fas fa-microchip"></i>
                    {model}
                  </span>
                )}
                <span>
                  <i className="fas fa-calendar-alt"></i>
                  {formatFecha(assistant.fechaAlta || assistant.FechaAlta)}
                </span>
                {password && (
                  <span>
                    <i className="fas fa-lock"></i>
                    Con password
                  </span>
                )}
              </div>

              <div className="assistant-card-footer">
                {renderAssistantActions(assistant, 'card')}
              </div>
            </article>
          );
        })}
      </div>
    );
  };

  const handleAdminMenuSelect = (item) => {
    setIsSidebarOpen(false);
    navigateAdminMenuItem(navigate, item.id);
  };

  const handleProfessorMenuSelect = (item) => {
    setIsSidebarOpen(false);
    navigateProfessorMenuItem(navigate, item.id);
  };

  return (
    <div className={`new-dashboard my-assistants ${isAdminUser ? 'admin-mode' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {isAdminUser ? (
            <AdminRoleMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
              isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
              activeItemId={isRagFlowProvider ? 'asistentes-ragflow' : 'asistentes'}
              onSelect={handleAdminMenuSelect}
            />
          ) : (
            <ProfessorMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
              isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
              activeItemId={isRagFlowProvider ? 'mis-ragflow-asistentes' : 'mis-asistentes'}
              onSelect={handleProfessorMenuSelect}
            />
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || user?.roleName || user?.nombreRol || 'Usuario'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Alternar menú lateral"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              Mis Asistentes {providerLabel}
            </h1>
            <p className="page-subtitle">Gestiona tus asistentes {providerLabel}</p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
            
            <div className="user-menu">
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {/* Assistants Summary */}
          <div className="assistants-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fas fa-robot"></i>
              </div>
              <div className="summary-content">
                <h2 className="summary-number">{asistentes.length}</h2>
                <p className="summary-text">
                  {asistentes.length === 1 ? 'Asistente creado' : 'Asistentes creados'}
                </p>
                <div className="summary-description">
                  {asistentes.length === 0 
                    ? 'Aún no has creado ningún asistente IA'
                    : 'Listos para ayudar a los alumnos'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* CTA Nuevo asistente */}
          {(isAdminUser || isCurrentAssistantFeatureEnabled) && (
            <div className="assistant-cta">
              <div className="cta-text">
                <h3>Crea un nuevo asistente {providerLabel}</h3>
                <p>Comparte material y habilítalo para tus alumnos en segundos.</p>
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleNewAssistant}
              >
                <i className="fas fa-plus"></i>
                Nuevo {providerLabel}
              </button>
            </div>
          )}

          <section className="assistants-browser">
            <div className="assistants-browser-header">
              <div>
                <h2 className="assistants-browser-title">
                  <i className={viewMode === 'cards' ? 'fas fa-th-large' : 'fas fa-list'}></i>
                  Asistentes {providerLabel}
                </h2>
                <p>
                  {filteredAsistentes.length} de {asistentes.length} asistentes
                  {hasActiveSearch ? ' encontrados' : ''}
                </p>
              </div>
              <div className="assistants-view-toggle" role="group" aria-label="Cambiar vista">
                <button
                  type="button"
                  className={viewMode === 'cards' ? 'active' : ''}
                  onClick={() => setViewMode('cards')}
                  title="Ver como cards"
                >
                  <i className="fas fa-th-large"></i>
                  <span>Cards</span>
                </button>
                <button
                  type="button"
                  className={viewMode === 'table' ? 'active' : ''}
                  onClick={() => setViewMode('table')}
                  title="Ver como grilla"
                >
                  <i className="fas fa-table"></i>
                  <span>Grilla</span>
                </button>
              </div>
            </div>

            <div className="assistants-toolbar">
              <div className="assistants-search">
                <i className="fas fa-search" aria-hidden="true"></i>
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por nombre, descripcion, materia o area"
                  aria-label="Buscar asistentes"
                />
                {hasActiveSearch && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    title="Limpiar busqueda"
                  >
                    <i className="fas fa-times" aria-hidden="true"></i>
                  </button>
                )}
              </div>
            </div>

            {viewMode === 'cards' ? (
              renderAssistantCards()
            ) : (
              <DataTable
                data={filteredAsistentes}
                columns={columns}
                loading={isLoading}
                loadingMessage="Obteniendo mis asistentes..."
                emptyMessage={emptyAssistantsMessage}
                className="assistants-table"
                getRowClassName={getAssistantRowClassName}
              />
            )}
          </section>
        </div>
      </main>

      {assistantToDelete && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>¿Eliminar asistente?</h3>
            <p>A continuación, aceptando, usted eliminará el asistente junto con sus archivos, esta acción no se podrá deshacer. ¿Está usted seguro?</p>
            <div className="confirm-modal-actions">
              <button className="btn btn-outline" type="button" onClick={closeDeleteModal}>
                Cancelar
              </button>
              <button className="btn btn-danger" type="button" onClick={handleConfirmDelete}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {warningModal.open && (
        <div className="ave-warning-modal-overlay" onClick={closeWarningModal}>
          <div
            className="ave-warning-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ave-warning-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ave-warning-modal-header">
              <div>
                <p className="ave-warning-modal-eyebrow">Advertencia</p>
                <h3 id="ave-warning-modal-title">{warningModal.assistantName}</h3>
              </div>
              <button
                type="button"
                className="ave-warning-modal-close"
                onClick={closeWarningModal}
                title="Cerrar"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="ave-warning-modal-content">
              {warningModal.detail}
            </div>
            <div className="ave-warning-modal-actions">
              <button className="btn btn-primary" type="button" onClick={closeWarningModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      <ChatModal
        isOpen={showChatModal}
        onClose={handleCloseChatModal}
        assistant={selectedAssistant}
      />
      {isDeleting && (
        <LoadingSpinner message="Eliminando asistente..." />
      )}
    </div>
  );
};

export default MyAssistants;
